import * as LDClient from 'launchdarkly-js-client-sdk'
import { setItemToStorage, getItemFromStorage } from './storage'

let client

const createRandomKey = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const createContextKeyAndSave = () => {
  const UUID = createRandomKey()
  setItemToStorage('launchDarklyKey', UUID)
  return UUID
}

export const getContextKeyFromLocalStorage = () => {
  return getItemFromStorage('launchDarklyKey')
}

export const updateContextKeyAndSave = newKey => {
  setItemToStorage('launchDarklyKey', newKey)
}

export const getDarklyClient = () => {
  return client
}

const buildDarklyContext = ({ isAuthenticated = false, currentUser = {} } = {}) => {
  const currentKey = getContextKeyFromLocalStorage()
  const deviceKey = currentKey || createContextKeyAndSave()

  const context = {
    kind: 'multi',
    device: {
      kind: 'device',
      key: deviceKey,
      anonymous: true,
    },
  }

  if (isAuthenticated && currentUser.id) {
    const profileData = (currentUser.profile && currentUser.profile.data) || {}
    context.user = {
      kind: 'user',
      key: currentUser.id,
      email: currentUser.email,
      name: profileData.first_name
        ? `${profileData.first_name} ${profileData.last_name || ''}`.trim()
        : null,
      anonymous: false,
      _meta: {
        privateAttributes: ['email', 'name'],
      },
    }
  }

  return context
}

export const createDarklyClient = async ({ isAuthenticated = false, currentUser = {} } = {}) => {
  const clientSideKey = process.env.VUE_APP_DARKLY_CLIENT_SIDE_KEY
  if (!clientSideKey) return null

  try {
    const context = buildDarklyContext({ isAuthenticated, currentUser })

    if (client) {
      await client.identify(context)
    } else {
      client = LDClient.initialize(clientSideKey, context)
    }

    await client.waitForInitialization()
    return client
  } catch (err) {
    throw err
  }
}

export const getAllDarklyFlags = () => {
  if (!client) return {}
  return client.allFlags()
}

export const onDarklyReady = callback => {
  if (!client) return null
  return client.on('ready', callback)
}

export const onDarklyFeatureFlagChange = (featureFlag, callback) => {
  if (!client) return null
  return client.on(`change:${featureFlag}`, callback)
}

export const getDarklyFlagKeyValue = async flagKey => {
  if (!client) return undefined

  await client.waitForInitialization()
  const result = client.variation(flagKey)
  return result
}

export const closeDarklyClient = async callback => {
  if (!client) return null
  const result = await client.close(callback)
  client = null
  return result
}

export const switchDarklyContextToAnonymous = async () => {
  if (!client) return null

  const currentKey = getContextKeyFromLocalStorage()
  const deviceKey = currentKey || createContextKeyAndSave()
  const context = {
    kind: 'multi',
    device: {
      kind: 'device',
      key: deviceKey,
      anonymous: true,
    },
  }

  await client.identify(context)
  return context
}
